import React, { createContext, useContext, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=authed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("tc_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Only a real 401 ends the session; network blips / 429 / 5xx are retried and keep the token.
    let cancelled = false;
    const check = async (attempt) => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) { setUser(r.data.user); setLoading(false); }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          localStorage.removeItem("tc_token");
          setUser(false); setLoading(false);
        } else if (attempt < 5) {
          setTimeout(() => check(attempt + 1), 800 * (attempt + 1));
        } else {
          setUser(false); setLoading(false);
        }
      }
    };
    check(0);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {  // streak days follow the learner's own calendar day
    if (!user) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) api.post("/xp/timezone", { timezone: tz }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("tc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, role) => {
    const { data } = await api.post("/auth/register", { name, email, password, role });
    localStorage.setItem("tc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("tc_token");
    setUser(false);
  };

  const refreshUser = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data.user);
    return r.data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { apiErr };
